import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, queryAll, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksPageChangeEvent } from '../../events/';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../contextual-menu/contextual-menu';
import { BlocksContextualMenu } from '../contextual-menu/contextual-menu';
import '../dropdown/dropdown';
import { PartialDataSource } from '../dropdown/dropdown.model';
import '../icon/icons/chevron-left';
import '../icon/icons/chevron-right';
import '../icon/icons/ellipsis';
import '../list-item/list-item';
import '../list/list';
import '../pagination-item/pagination-item';
import { BlocksPaginationItem } from '../pagination-item/pagination-item';
import type { PaginationVariant } from './pagination.model';
import styles from './pagination.styles';

/**
 * For accessing the position of ellipsis
 * 1 dots with no prefix and suffix space
 * 2 dots with prefix space
 * 3 dots with suffix space
 */
const dot = '...'; /**1 */
const initialDot = ' ...'; /**2 */
const endDot = '... '; /**3 */

/**
 * @slot default - The pagination content
 * @slot label - Content describes pagination with text
 * @slot prev -  Content appearing before the default slot
 * @slot next -  Content appearing after the default slot
 * @slot inpage-label -  Content describes pagination with text in in-page variant
 * @fires bl-page-change - emitted when pageItem/dropdown value is changed
 * @csspart base - The component's base wrapper.
 * @csspart in-page - The component's in-page.
 * @csspart part-inpage-label - The component's inpage-label slot.
 * @csspart footer-mobile - The component's footer-mobile.
 * @csspart footer - The component's footer.
 * @csspart label-wrap - The component's label wrapper.
 * @csspart part-label - The component's label slot.
 * @csspart label-text - The component's label-text.
 * @csspart list - The component's list.
 * @csspart left-chevron - The component's left-chevron.
 * @csspart prev-text - The component's prev-text.
 * @csspart part-prev - The component's prev slot.
 * @csspart prev-icon - The component's `<cat-icon-chevron-left>` icon.
 * @csspart page - The component's page item with context menu.
 * @csspart more - The component's more element.
 * @csspart menu-trigger - The component's menu-trigger.
 * @csspart ellipsis-icon - The component's `<cat-icon-ellipsis>` icon.
 * @csspart more-list - The component's more-list.
 * @csspart more-list-item - The component's more-list-item.
 * @csspart text - The component's text.
 * @csspart right-chevron - The component's right-chevron.
 * @csspart next-text - The component's next-text.
 * @csspart part-next - The component's next element.
 * @csspart next-icon - The component's `<cat-icon-chevron-right>` icon.
 * @csspart cat_c_pagination_dropdown - The component's dropdown wrapper.
 * @csspart show-text - The component's  dropdown text.
 * @csspart page-dropdown - The component's dropdown.
 * @csspart list - The component's list element.
 * @csspart list-item - The component's list-item element.

 */
export class BlocksPagination extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Previous button text
   */
  @property({ type: String })
  prevButtonText: string | undefined;

  /**
   * Next button text
   */
  @property({ type: String })
  nextButtonText: string | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   *Data size
   */
  @property({ type: Number })
  totalRecords!: number;

  /**
   *Current PageNumber
   */
  @property({ type: Number })
  currentItem = 1;

  /**
   *Records per page
   */
  @property({ type: Number })
  pageSize: number | undefined;

  /**
   *Hide page Size
   */
  @property({ type: Boolean })
  hidePageSize: boolean | undefined;

  /**
   *Dropdown option for choosing pageItemsRange to be showed per page
   */
  @property({ type: Array })
  pageSizeOptions: Array<number> = [20, 40, 60, 80, 100];

  /**
   *Pagination variant
   */
  @property({ type: String })
  variant: PaginationVariant;

  /**
   *Label for dropdown
   */
  @property({ type: String })
  pageSizeLabel = 'Show';

  /**
   * **Breakpoint below which pagination switches to mobile view.Defaults to 786px.**
   */
  @property({ type: Number })
  mobileBreakPoint = 768;

  /**
   * Truthy value enables mobile view of pagination
   */
  @state()
  private isMobile = false;

  /**
   * displaying page numbers in as pagination
   */
  @state() private pageItems: string[] = [];

  private recordsPerPageDropdownData: PartialDataSource[] | undefined;

  /**
   * Based on total records create num of pages list
   */
  private pages: string[] = [];

  /**
   * Page Numbers that will be displayed under ellipsis
   */
  @state() private pageItemsRange: string[] = [];

  private menuOpen = false;

  @queryAll('cat-contextual-menu')
  private _catContextualMenus: NodeListOf<BlocksContextualMenu> | undefined;

  @queryAll('.cat-c-pagination-item-page')
  private _catPaginationItems!: NodeListOf<BlocksPaginationItem>;
  /**
   * Returning current screen width
   */
  private get screenWidth(): number {
    return Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.checkForMobileView);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.checkForMobileView);
  }

  /**
   * Setting Screen width on reset
   */
  private checkForMobileView = (): void => {
    this.isMobile = this.screenWidth <= this.mobileBreakPoint;
  };

  /**
   * Close contextual menu when we select pageNo in the ellipsis dropdown
   */
  private async handleKeyDown(evt: KeyboardEvent, selectedItem: string): Promise<void> {
    if (evt.key === KEYS.ENTER || evt.key === KEYS.SPACE) {
      if (!this.menuOpen) {
        evt.preventDefault();
      }
      this.handlePageItemClick(selectedItem);
      await this.updateComplete;
      if (selectedItem == endDot) {
        this._catContextualMenus?.[1].open();
        this.menuOpen = true;
      } else if (selectedItem == initialDot) {
        this._catContextualMenus?.[0].open();
        this.menuOpen = true;
      } else if (selectedItem == dot) {
        this._catContextualMenus?.forEach((menu) => menu.open());
        this.menuOpen = true;
      }
    }
  }

  /**
   * navigate to next/previous item when user Presses Enter on Right/Left chevron Icons Respectively
   */
  private handleKeyDownOnChevron(e: KeyboardEvent, icon: string) {
    if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
      if (icon === 'chevronLeft') {
        this.leftChevronClick(this.currentItem);
      } else {
        this.rightChevronClick(this.currentItem);
      }
    }
  }
  /**
   *Text with pageItemsRange of numbers based on current page no.
   *1) Condition to Display Viewing text in default variant for desktop
   */
  private get label() {
    return html`
      ${this.variant !== 'in-page' && !this.isMobile ? html`<span>Viewing</span>` : nothing /* 1*/}
      ${this.pageSize && this.totalRecords != 0 ? this.currentItem * this.pageSize - this.pageSize + 1 : this.totalRecords} -
      ${this.pageSize && this.currentItem * this.pageSize < this.totalRecords ? this.currentItem * this.pageSize : this.totalRecords}
      <span>of</span> ${this.totalRecords}
    `;
  }

  /**
   * left chevron icon click
   */
  private leftChevronClick(pageNo: number) {
    if (this.currentItem !== 1) {
      this.handlePageItemClick(pageNo - 1);
    }
  }
  /**
   * right chevron icon click
   */
  private rightChevronClick(pageNo: number) {
    if (pageNo !== this.pages.length) {
      this.handlePageItemClick(pageNo + 1);
    }
  }

  /**
   *Text with pageItemsRange along with the previous and next button
   */
  private renderSmallPager() {
    return html`
      <ol part="list" role="list" class="cat-c-pagination__list">
        <cat-pagination-item
          part="left-chevron"
          exportparts=${this.getExportParts('pagination-item-left-chevron', 'pagination-item')}
          @click=${() => this.leftChevronClick(this.currentItem)}
          .text=${`${this.currentItem - 1}`}
          ?disabled=${this.currentItem == 1}
          @keydown=${(e: KeyboardEvent) => this.handleKeyDownOnChevron(e, 'chevronLeft')}
        >
          <slot part="part-prev" name="prev">
            <cat-icon-chevron-left part="prev-icon" exportparts=${this.getExportParts('chevron-left', 'icon')}></cat-icon-chevron-left>
          </slot>
        </cat-pagination-item>
        <slot part="part-inpage-label" name="inpage-label">
          <label class="cat-c-pagination__label" part="label-text"> ${this.label} </label>
        </slot>
        <cat-pagination-item
          part="right-chevron"
          exportparts=${this.getExportParts('pagination-item-right-chevron', 'pagination-item')}
          @click=${() => this.rightChevronClick(this.currentItem)}
          @keydown=${(e: KeyboardEvent) => this.handleKeyDownOnChevron(e, 'chevronRight')}
          .text=${`${this.currentItem + 1}`}
          ?disabled=${this.currentItem == this.pages.length}
        >
          <slot part="part-next" name="next">
            <cat-icon-chevron-right part="next-icon" exportparts=${this.getExportParts('chevron-right', 'icon')}></cat-icon-chevron-right>
          </slot>
        </cat-pagination-item>
      </ol>
    `;
  }
  /**
   * Get the current pagination item from the list and focus it
   */
  private focusCurrentPaginationItem() {
    const currentPaginationItem = Array.from(this._catPaginationItems).find((paginationItem) => paginationItem.text === this.currentItem.toString());
    currentPaginationItem?.focus();
  }
  /**
   * Clicking on PageNo
   * 1 Checking whether the Item is ellipsis, if , flag is updated
   * 2 If the item is number, the currentItem is updated
   */

  private handlePageItemClick = async (selectedItem: string | number) => {
    if (isNaN(selectedItem as any)) {
      this.createPageItemRange(selectedItem as string);
    } else {
      this.currentItem = +selectedItem; /**2 */
      this.displayPageItems();
      this.eventDispatch();
      await this.updateComplete;
      this.focusCurrentPaginationItem();
    }
  };

  /**
   * Calculating the pageItemsRange for ellipsis
   * 1 Find if the currentItem is ellipsis
   * 2 Find the IndexOf the ellipsis
   * 3 pageItemsRange calculated by knowing the before and after element of ellipsis in Pages Array
   */
  private createPageItemRange(selectedItem: string) {
    let dotIndex;
    if (selectedItem == dot) {
      /**1 */
      dotIndex = this.pageItems.indexOf(dot); /**2 */
      this.pageItemsRange = this.pages.slice(parseInt(this.pageItems[dotIndex - 1]), parseInt(this.pageItems[dotIndex + 1]) - 1); /**3 */
    } else if (selectedItem == initialDot) {
      dotIndex = this.pageItems.indexOf(initialDot); /**2 */
      this.pageItemsRange = this.pages.slice(parseInt(this.pageItems[dotIndex - 1]), parseInt(this.pageItems[dotIndex + 1]) - 1);
    } else if (selectedItem == endDot) {
      dotIndex = this.pageItems.indexOf(endDot); /**2 */
      this.pageItemsRange = this.pages.slice(parseInt(this.pageItems[dotIndex - 1]), parseInt(this.pageItems[dotIndex + 1]) - 1);
    }
  }

  private eventDispatch() {
    this.dispatch({
      eventName: 'bl-page-change',
      detailObj: { pageNumber: this.currentItem, pageSize: this.pageSize, totalRecordSize: this.totalRecords }
    }) as BlocksPageChangeEvent;
  }

  /**
   * Dropdown with pageSizeOptions values
   * The selected item is updated as pageSize
   * Prevent event while same as previous pageSize value
   */
  private handleDropdownItemClick = (option: number) => {
    if (this.pageSize != option) {
      this.pageSize = option;
      this.currentItem = 1;
      this.eventDispatch();
    }
  };

  /**
   * Calculating the no.of Pages
   * 1 If no.of Pages is less than or equal to 6, show all page numbers
   * 2 Else, based on CurrentItem the array gets updated.
   */
  private displayPageItems() {
    const len = this.pages.length;
    if (this.pages.length <= 6) {
      this.pageItems = this.pages; /**1 */
    } else if (this.currentItem >= 1 && this.currentItem <= 3) {
      this.pageItems = ['1', '2', '3', '4', dot, len.toString()]; /**2 */
    } else if (this.currentItem > 3 && this.currentItem < 5) {
      const sliced = this.pages.slice(0, 5);
      this.pageItems = [...sliced, dot, len.toString()]; /**2 */
    } else if (this.currentItem > 4 && this.currentItem < this.pages.length - 2) {
      const sliced1 = this.pages.slice(this.currentItem - 2, this.currentItem);
      const sliced2 = this.pages.slice(this.currentItem, this.currentItem + 1);
      this.pageItems = ['1', initialDot, ...sliced1, ...sliced2, endDot, len.toString()]; /**2 */
    } else if (this.currentItem > this.pages.length - 3) {
      const sliced = this.pages.slice(this.pages.length - 4);
      this.pageItems = ['1', dot, ...sliced]; /**2 */
    }
  }

  /**
   * The pagination array created based on pageLength calculated from total record size
   */
  private createPageNumbers() {
    const pageLength = this.findPagesLength() || 0;
    for (let i = 1; i <= pageLength; i++) {
      this.pages.push(i.toString());
    }
  }

  /**
   * Calculating the pageItemsRange numbers to be displayed in label. Quotient is the total length of the pagination array.
   * If remainder is not zero, pagination array will have extra one page.
   */
  private findPagesLength() {
    let quotient = this.totalRecords;
    let remainder = this.pageSize ? this.totalRecords % this.pageSize : 0;
    if (this.pageSize === 0) {
      return null;
    } else {
      quotient = this.pageSize ? this.totalRecords / this.pageSize : 0;
    }

    if (remainder !== 0) {
      quotient = Math.floor(quotient) + 1;
    }
    return quotient;
  }

  /**partial data for dropdown*/
  private createRecordsPerPageDropdownData() {
    this.recordsPerPageDropdownData = this.pageSizeOptions.map((item) => {
      const obj = {
        label: item.toString(), //since dropdown component dataSource props expects label as string , so here we are converting to string
        value: item
      };
      return obj;
    });
  }
  /**
   * 1 set the default value of pageItemsRange
   * 2 If set of pageItemsRanges are given, the first value will be set as pageItemsRange
   */
  protected firstUpdated() {
    this.pageSize = this.pageSize || this.pageSizeOptions[0];
    this.createPageNumbers();
    this.createRecordsPerPageDropdownData();
    this.checkForMobileView();
  }

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('pageSize') || changedProperties.has('totalRecords')) {
      this.pageItems = [];
      this.pages = [];
      let currentItem = changedProperties.has('totalRecords') ? this.currentItem : 1;
      if (changedProperties.has('currentItem')) {
        currentItem = this.currentItem;
      }
      this.currentItem = currentItem;
      this.createPageNumbers();
      this.displayPageItems();
    }

    if (changedProperties.has('pageSizeOptions')) {
      this.createRecordsPerPageDropdownData();
    }

    if (changedProperties.has('currentItem')) {
      this.displayPageItems();
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-pagination', {
      'cat-c-pagination--has-before': this.slotNotEmpty('beforePagination'),
      'cat-c-pagination--has-after': this.slotNotEmpty('afterPagination'),
      'cat-c-pagination--inpage': this.variant == 'in-page',
      'cat-c-pagination--hide-page-size': this.hidePageSize == true,
      'cat-is-mobile': this.isMobile
    });

    return html`
      <nav part="base" class="${componentClassNames}" aria-label=${ifDefined(this.arialabel)}>
        ${this.variant === 'in-page'
          ? html` <div part="in-page" class="cat-pagination-inpage">${this.renderSmallPager()}</div> `
          : html`
              <div part="footer-mobile" class="cat-pagination-footer-mobile">${this.renderSmallPager()}</div>

              <div part="footer" class="cat-pagination-footer">
                <div part="label-wrap" class="cat-c-pagination-label">
                  <slot part="part-label" name="label"><label part="label-text">${this.label}</label></slot>
                </div>
                <ol part="list" role="list" class="cat-c-pagination__list">
                  <cat-pagination-item
                    part="left-chevron"
                    exportparts=${this.getExportParts('pagination-item-left-chevron', 'pagination-item')}
                    @click=${() => this.leftChevronClick(this.currentItem)}
                    .text=${`${this.currentItem - 1}`}
                    ?disabled=${this.currentItem == 1}
                    @keydown=${(e: KeyboardEvent) => this.handleKeyDownOnChevron(e, 'chevronLeft')}
                  >
                    <span part="prev-text" class="cat-u-is-vishidden"> ${this.prevButtonText ? this.prevButtonText : html` Previous `} </span>
                    <slot part="part-prev" name="prev">
                      <cat-icon-chevron-left part="prev-icon" exportparts=${this.getExportParts('chevron-left', 'icon')}></cat-icon-chevron-left>
                    </slot>
                  </cat-pagination-item>
                  ${this.pageItems.map(
                    (item: string) =>
                      html`
                        <cat-pagination-item
                          part="page"
                          exportparts=${this.getExportParts('pagination-item-page', 'pagination-item')}
                          class="cat-c-pagination-item-page"
                          .text=${item}
                          .divide=${isNaN(+item)}
                          @click=${() => {
                            this.handlePageItemClick(item);
                          }}
                          .isActive=${this.currentItem.toString() === item}
                          @keydown=${(e: KeyboardEvent) => this.handleKeyDown(e, item)}
                        >
                          ${isNaN(+item) //if item is in "..." pattern
                            ? html`
                                <cat-contextual-menu
                                  part="more"
                                  exportparts=${this.getExportParts('contextual-menu')}
                                  class="cat-c-contextual-pagination-item"
                                  ?hasScroll=${this.pageItemsRange.length >= 4}
                                  @bl-contextual-menu-open=${() => {
                                    this.menuOpen = true;
                                  }}
                                  @bl-contextual-menu-close=${() => {
                                    this.menuOpen = false;
                                  }}
                                >
                                  <div part="menu-trigger" role="button" aria-label="More items" slot="trigger">
                                    <cat-icon-ellipsis
                                      part="ellipsis-icon"
                                      exportparts=${this.getExportParts('ellipsis', 'icon')}
                                      slot="after"
                                    ></cat-icon-ellipsis>
                                  </div>
                                  <cat-list part="more-list" exportparts=${this.getExportParts('list-more', 'list')}>
                                    ${this.pageItemsRange.map((option, index) => {
                                      return html`
                                        <cat-list-item
                                          part="more-list-item"
                                          exportparts=${this.getExportParts('list-item-more', 'list-item')}
                                          class="cat-c-contextual-pagination-list-item"
                                          value=${option}
                                          key=${index}
                                          @click=${(e: MouseEvent) => {
                                            e.stopPropagation();
                                          }}
                                          @bl-select=${() => {
                                            this.handlePageItemClick(option);
                                          }}
                                        >
                                          <span part="text" class="cat-c-contextual-pagination-item-text">${option}</span>
                                        </cat-list-item>
                                      `;
                                    })}
                                  </cat-list>
                                </cat-contextual-menu>
                              `
                            : item}
                        </cat-pagination-item>
                      `
                  )}

                  <cat-pagination-item
                    part="right-chevron"
                    exportparts=${this.getExportParts('pagination-item-left-chevron', 'pagination-item')}
                    @click=${() => this.rightChevronClick(this.currentItem)}
                    .text=${`${this.currentItem + 1}`}
                    ?disabled=${this.currentItem == this.pages.length}
                    @keydown=${(e: KeyboardEvent) => this.handleKeyDownOnChevron(e, 'chevronRight')}
                  >
                    <span part="next-text" class="cat-u-is-vishidden"> ${this.nextButtonText ? this.nextButtonText : html` Next `} </span>
                    <slot part="part-next" name="next">
                      <cat-icon-chevron-right part="next-icon" exportparts=${this.getExportParts('chevron-right', 'icon')}></cat-icon-chevron-right>
                    </slot>
                  </cat-pagination-item>
                </ol>
                <div class="cat-c-pagination-dropdown" part="cat_c_pagination_dropdown">
                  <span part="show-text" class="dropdown-show-text">${this.pageSizeLabel}</span>
                  <cat-dropdown
                    part="page-dropdown"
                    exportparts=${this.getExportParts('dropdown')}
                    size="md"
                    fieldId="dropdown-1"
                    name="dropdown-1"
                    hideLabel
                    .value=${`${this.pageSize}`}
                  >
                    <cat-list part="list" exportparts=${this.getExportParts('list-dropdown', 'list')}>
                      ${this.recordsPerPageDropdownData?.map(
                        (option, index) =>
                          html`
                            <cat-list-item
                              part="list-item"
                              exportparts=${this.getExportParts('list-item-dropdown', 'list-item')}
                              value=${option.label}
                              key=${index}
                              @click=${() => {
                                this.handleDropdownItemClick(option.value);
                              }}
                              >${option.label}
                            </cat-list-item>
                          `
                      )}
                    </cat-list>
                  </cat-dropdown>
                </div>
              </div>
            `}
      </nav>
    `;
  }
}

if (customElements.get('cat-pagination') === undefined) {
  customElements.define('cat-pagination', BlocksPagination);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-pagination': BlocksPagination;
  }
}
